import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useClinicalListsListPeople } from '~/backend/connector-next-api/connector-next-components';
import type { PatientDetailDto } from '~/backend/connector-next-api/connector-next-schemas';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { SeparatorDot } from '~/components/Separated';
import {
    PERMISSION,
    useConnectorIdentity,
} from '~/hooks/auth/useConnectorIdentity';
import { useToday } from '~/hooks/useToday';
import { useClinicalLists, usePatientIntake } from '../hooks';
import {
    AssigneeAvatar,
    AssigneePicker,
    ChoicePicker,
    CourseStagePicker,
    CourseStageStrip,
    PatientIdText,
    personChoices,
    QuietButton,
    SiteName,
    TreatmentStart,
    TreatmentStartField,
    TreatmentStartNote,
    usePatientCare,
    type Choice,
} from '../shared';
import {
    ageInYears,
    configuredOptions,
    courseStageLabelId,
    formatDayText,
    formatPatientName,
    initialsOf,
    offeredEntries,
    patientAssignee,
    patientCourseStagesDone,
    resolveCourseStage,
    sexAgeText,
    type CourseStage,
} from '../model';
import PatientTagsField from './PatientTagsField';

type DrilldownHeaderProps = {
    /** Undefined while the patient detail query is still loading. */
    patient: PatientDetailDto | undefined;
    onClose: () => void;
    /** Sets the patient's course stage and done-set; absent for a user who may not change patients. */
    onSetCourseStage:
        | ((change: {
              stage: CourseStage;
              done: readonly CourseStage[];
          }) => void)
        | undefined;
    /** True while any write to the patient is in flight. */
    stageSaving: boolean;
    /** The panel's actions, at the top beside the close control. */
    actions?: ReactNode;
};

/**
 * Panel header: patient identity, the course facts (stage, assignee,
 * treatment start and last access, then site, radiation oncologist and
 * planner), the clinical tags, the notes when there are any, and the course
 * stage with the stages done, which the user sets here. A user who may assign
 * patients assigns it here, to themself or anybody else. A user who may
 * change patients edits the intake here, one value at a time, with the values
 * the device suggests labelled as such until somebody chooses one.
 * Identity and notes are hidden with sensitive info.
 */
export default function DrilldownHeader({
    patient,
    onClose,
    onSetCourseStage,
    stageSaving,
    actions,
}: DrilldownHeaderProps) {
    const intl = useIntl();
    const today = useToday();
    const { data: lists } = useClinicalLists();
    const { data: people } = useClinicalListsListPeople({});
    const identity = useConnectorIdentity();
    const canAssign = identity.canAssignPatients;
    const care = usePatientCare(patient ?? {}, lists, people);
    // The intake stays editable after the workflow starts: the start date and
    // the people move as the course is planned.
    const canEditIntake =
        identity.can(PERMISSION.patientModify) && patient !== undefined;
    const intake = usePatientIntake(
        patient,
        lists.sites ?? [],
        people?.meId ?? undefined
    );
    const notChosen = intl.formatMessage({ id: 'patientList.notChosen' });
    const suggestedLabel = intl.formatMessage({
        id: 'patientList.intake.suggested',
    });
    const siteChoices: Choice[] = configuredOptions([
        ...intake.suggestedSites,
        ...offeredEntries(lists.sites ?? []).filter(
            (site) => !intake.suggestedSites.includes(site)
        ),
    ]).map((option) => ({
        ...option,
        detail: intake.suggestedSites.some((site) => site.id === option.value)
            ? suggestedLabel
            : undefined,
    }));
    const name = formatPatientName(patient?.dicomPatientName);
    const stage = resolveCourseStage(patient?.courseStage);
    const stagesDone = patientCourseStagesDone(patient?.courseStagesDone);
    // A write needs the version to guard against; without one it is refused.
    const stageDisabled = stageSaving || patient?.version == null;
    const currentDone = stagesDone.includes(stage);
    const toggleDone = (toggled: CourseStage) =>
        onSetCourseStage?.({
            stage,
            done: stagesDone.includes(toggled)
                ? stagesDone.filter((value) => value !== toggled)
                : [...stagesDone, toggled],
        });
    const age = ageInYears(patient?.birthDate, new Date());
    const birthDay = formatDayText(intl, patient?.birthDate);
    const lastAccessed = formatDayText(intl, patient?.lastAccessedAt);
    const sexAge = sexAgeText(intl, patient?.sex, age);
    const assignee = patient && patientAssignee(intl, people, patient);

    return (
        <Box
            sx={{
                px: 3,
                pt: 2.5,
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                    aria-hidden
                    sx={(theme) => ({
                        width: 48,
                        height: 48,
                        fontSize: 16,
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.18),
                        color: 'primary.light',
                    })}
                >
                    <SensitiveInfoTypography>
                        {name ? initialsOf(name) : ''}
                    </SensitiveInfoTypography>
                </Avatar>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        noWrap
                        sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}
                    >
                        <SensitiveInfoTypography>
                            {name || '…'}
                        </SensitiveInfoTypography>
                    </Typography>
                    {patient && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontFamily: 'fontFamilyMonospace',
                                fontSize: 13,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            <PatientIdText id={patient.dicomPatientId} />
                            {sexAge && (
                                <>
                                    <SeparatorDot />
                                    <SensitiveInfoTypography>
                                        {sexAge}
                                    </SensitiveInfoTypography>
                                </>
                            )}
                            {birthDay && (
                                <>
                                    <SeparatorDot />
                                    <span>
                                        <FormattedMessage
                                            id="patientList.drilldown.dob"
                                            values={{
                                                date: (
                                                    <SensitiveInfoTypography>
                                                        {birthDay}
                                                    </SensitiveInfoTypography>
                                                ),
                                            }}
                                        />
                                    </span>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
                {actions && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        {actions}
                    </Box>
                )}
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label={intl.formatMessage({
                        id: 'patientList.drilldown.close',
                    })}
                    title={intl.formatMessage({
                        id: 'patientList.drilldown.closeTitle',
                    })}
                    sx={{ color: 'text.secondary' }}
                >
                    <X size={18} />
                </IconButton>
            </Box>

            {patient && (
                <Box sx={FACTS_SX}>
                    <Fact
                        labelId="patientList.column.status"
                        value={
                            onSetCourseStage ? (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <CourseStagePicker
                                        stage={stage}
                                        done={stagesDone}
                                        onChange={onSetCourseStage}
                                        disabled={stageDisabled}
                                    />
                                    <QuietButton
                                        disabled={stageDisabled}
                                        aria-pressed={currentDone}
                                        onClick={() => toggleDone(stage)}
                                    >
                                        {intl.formatMessage({
                                            id: currentDone
                                                ? 'patientList.stage.markNotDone'
                                                : 'patientList.stage.markDone',
                                        })}
                                    </QuietButton>
                                </Box>
                            ) : (
                                intl.formatMessage({
                                    id: courseStageLabelId(stage),
                                })
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.column.assignee"
                        value={
                            (assignee || canAssign) && (
                                // Wraps rather than spilling into the next
                                // fact.
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {canAssign ? (
                                        <AssigneePicker
                                            patient={patient}
                                            people={people}
                                        />
                                    ) : (
                                        assignee && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    minWidth: 0,
                                                    maxWidth: '100%',
                                                }}
                                            >
                                                <AssigneeAvatar
                                                    assignee={assignee}
                                                />
                                                <Typography
                                                    component="span"
                                                    noWrap
                                                    fontSize={14}
                                                >
                                                    {assignee.name}
                                                </Typography>
                                            </Box>
                                        )
                                    )}
                                </Box>
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.column.treatmentStart"
                        value={
                            canEditIntake ? (
                                <>
                                    <TreatmentStartField
                                        edit={intake.treatmentStart}
                                        disabled={intake.disabled}
                                    />
                                    <TreatmentStartNote
                                        patient={patient}
                                        today={today}
                                    />
                                </>
                            ) : (
                                patient.treatmentStart && (
                                    <TreatmentStart
                                        patient={patient}
                                        today={today}
                                    />
                                )
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.drilldown.lastAccessed"
                        value={lastAccessed}
                        mono
                    />
                </Box>
            )}

            {patient && (
                <Box sx={FACTS_SX}>
                    <Fact
                        labelId="patientList.site"
                        value={
                            canEditIntake ? (
                                <ChoicePicker
                                    name={intl.formatMessage({
                                        id: 'patientList.site',
                                    })}
                                    choices={siteChoices}
                                    value={intake.shown.siteId}
                                    placeholder={notChosen}
                                    unknownLabel={care.siteName}
                                    suggestion={
                                        intake.siteSuggested
                                            ? {
                                                  label: suggestedLabel,
                                                  hint: intl.formatMessage(
                                                      {
                                                          id: 'patientList.intake.suggestedFrom',
                                                      },
                                                      {
                                                          source: patient.bodyParts?.join(
                                                              ', '
                                                          ),
                                                      }
                                                  ),
                                              }
                                            : undefined
                                    }
                                    disabled={intake.disabled}
                                    onChange={(siteId) =>
                                        intake.choose({ siteId })
                                    }
                                />
                            ) : (
                                <SiteName
                                    name={care.siteName}
                                    color={care.siteColor}
                                />
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.radiationOncologist"
                        value={
                            canEditIntake ? (
                                <ChoicePicker
                                    name={intl.formatMessage({
                                        id: 'patientList.radiationOncologist',
                                    })}
                                    choices={personChoices(people?.people)}
                                    value={intake.shown.radiationOncologistId}
                                    placeholder={notChosen}
                                    unknownLabel={care.oncologist}
                                    disabled={intake.disabled}
                                    onChange={(radiationOncologistId) =>
                                        intake.choose({ radiationOncologistId })
                                    }
                                />
                            ) : (
                                care.oncologist
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.planner"
                        value={
                            canEditIntake ? (
                                <ChoicePicker
                                    name={intl.formatMessage({
                                        id: 'patientList.planner',
                                    })}
                                    choices={personChoices(people?.people)}
                                    value={intake.shown.plannerId}
                                    placeholder={notChosen}
                                    unknownLabel={care.planner}
                                    suggestion={
                                        intake.plannerSuggested
                                            ? {
                                                  label: suggestedLabel,
                                                  hint: intl.formatMessage({
                                                      id: 'patientList.intake.suggestedMe',
                                                  }),
                                              }
                                            : undefined
                                    }
                                    disabled={intake.disabled}
                                    onChange={(plannerId) =>
                                        intake.choose({ plannerId })
                                    }
                                />
                            ) : (
                                care.planner
                            )
                        }
                    />
                </Box>
            )}

            {patient && <PatientTagsField patient={patient} />}

            {patient?.notes && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                    }}
                >
                    <LabelText>
                        <FormattedMessage id="patientList.drilldown.fact.notes" />
                    </LabelText>
                    {/* Free text, so it may name anything about the patient:
                        hidden with the identity, and kept as it was typed. */}
                    <ValueText
                        component="span"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        <SensitiveInfoTypography>
                            {patient.notes}
                        </SensitiveInfoTypography>
                    </ValueText>
                </Box>
            )}

            {patient && (
                <Box sx={{ pt: 0.5 }}>
                    <CourseStageStrip
                        stage={stage}
                        done={stagesDone}
                        size="large"
                        disabled={stageDisabled}
                        onToggleDone={onSetCourseStage && toggleDone}
                        onSelect={
                            onSetCourseStage &&
                            ((selected) =>
                                onSetCourseStage({
                                    stage: selected,
                                    done: stagesDone,
                                }))
                        }
                    />
                </Box>
            )}
        </Box>
    );
}

/** Facts in columns, each label above its value; every line of facts shares the same columns. */
const FACTS_SX = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gridTemplateRows: 'auto auto',
    gridAutoFlow: 'column',
    columnGap: 2,
    rowGap: '2px',
} as const;

/**
 * One labelled fact of the header grid, as its label and value cells; the
 * grid flows the label onto one row and the value onto the next, so a label
 * that wraps keeps every value aligned. An absent value renders as a dash.
 */
function Fact({
    labelId,
    value,
    mono = false,
}: {
    labelId: string;
    /** The fact, or nothing (undefined, null, '' or false) when the record holds none. */
    value: ReactNode;
    mono?: boolean;
}) {
    const present =
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== false;
    return (
        <>
            <LabelText sx={{ whiteSpace: 'normal', alignSelf: 'end' }}>
                <FormattedMessage id={labelId} />
            </LabelText>
            <ValueText
                component="span"
                sx={{
                    minWidth: 0,
                    fontFamily: mono ? 'fontFamilyMonospace' : undefined,
                }}
            >
                {present ? value : '—'}
            </ValueText>
        </>
    );
}
